import {
  TextField,
  SelectField,
  useRecordContext,
  TextInput,
  WithRecord,
  SelectArrayInput,
  NumberInput,
  DateTimeInput,
} from "react-admin";
import { useParams, Link } from "react-router-dom";

import BusinessIcon from "@mui/icons-material/Business";
import { JsonField } from "../ra-lb-tools/components/jsonField";
import { MyList } from "../ra-lb-tools/components/myList";
import get from "lodash/get";
import { Debug } from "../ra-lb-tools/components/debug";
import { Settings } from "../ra-lb-tools/utils/settings";
import { Box } from "@mui/material";
import { TowerTimezonedDateField } from "./tower/towerTimezonedDate";
import { TowerField, TowerInput } from "./tower/towers";
import {
  getRequestLogUri,
  getTimeRangeLogUri,
} from "../ra-lb-tools/utils/grafana";
import { RecordSummary, MyShow } from "../ra-lb-tools/components/MyShow";
import {
  MyTabbedShowLayout,
  Tab,
} from "../ra-lb-tools/components/myTabbedShowLayout";
import { LabeledColumn } from "../ra-lb-tools/components/labeledColumn";
import { UserField, UserInput } from "./users";
import { statusChoices } from "./tower/base";
import { MyBooleanField } from "../components/myBooleanField";
import { NullableBooleanField } from "../ra-lb-tools/components/nullableBooleanField";
import { ReferenceInputString } from "../ra-lb-tools/components/referenceInputString";
import {
  getFirst,
  getTimeDiffLabel,
  getValue,
} from "../ra-lb-tools/utils/tools";

export const SystemIcon = BusinessIcon;

/**
 * TODO:
 * Check for enhancement
 * Currently this needs to be manually synchronized from
 * hal-backend/app/app/models/tower_history.py::class TowerHistoryType(EnumBase)
 */
export const TowerHistoryType = {
  StatusChange: { id: 1, name: "Status Change" },
  FirmwareChange: { id: 2, name: "Firmware Change" },
  DeviceSerialChange: { id: 3, name: "Device Serial Change" },
  DeviceModelChange: { id: 4, name: "Device Model Change" },
  EventsResetUserReset: { id: 5, name: "Events Reset: User Reset" },
  EventsResetAutomaticReset: { id: 6, name: "Events Reset: Automatic Reset" },
  EventSync: { id: 7, name: "Event Sync" },
  ErrorEvent: { id: 8, name: "Error Event" },
};

export const typeChoices = Object.values(TowerHistoryType);

const towerHistoryFilters = (typeChoices) => [
  <NumberInput source="id" alwaysOn key={"Id"} />,
  <TowerInput
    label="Tower"
    source="towerId"
    alwaysOn
    key={"towerId"}
    filter={{ statusId: 0 }}
  />,
  <SelectArrayInput
    label="Type"
    source="type"
    choices={typeChoices}
    alwaysOn
    key={"type"}
  />,
  <UserInput
    source="userId"
    label="User"
    alwaysOn
    allowEmpty
    key={"user_id"}
  />,
  <TextInput source="deviceSerial" key={"device_serial"} />,
  <NullableBooleanField source="isForTest" alwaysOn key="isForTest" />,
  <ReferenceInputString
    source="summary"
    reference="event-sync-summary"
    alwaysOn
    key="event-sync-summary"
  />,
  <DateTimeInput
    label="From"
    source="timestamp_ge"
    alwaysOn
    key={"timestamp_ge"}
  />,
  <DateTimeInput
    label="To"
    source="timestamp_le"
    alwaysOn
    key={"timestamp_le"}
  />,
];

const getLogUri = (requestInfo) => {
  const request_id = requestInfo.id;
  const timestamp = requestInfo.timestamp;

  return getRequestLogUri(request_id, timestamp);
};

const getAproxLogUri = (
  record,
  { secondsBefore = 3, secondsAfter = 1, timestampSource = "timestamp" }
) => {
  const timestamp = get(record, timestampSource);
  return getTimeRangeLogUri(secondsBefore, secondsAfter, timestamp);
};

export const LogLink = ({ ...props }) => {
  const record = useRecordContext();
  const requestInfo = get(record, "new.request");

  if (!(requestInfo && requestInfo.id && requestInfo.timestamp)) {
    const logUri = getAproxLogUri(record, props);

    return (
      <Link to={logUri} target="_blank">
        Log Near
      </Link>
    );
  }

  const logUri = getLogUri(requestInfo);

  return (
    <Link to={logUri} target="_blank">
      Log
    </Link>
  );
};

const types_with_summary = [
  TowerHistoryType.EventSync.id,
  TowerHistoryType.ErrorEvent.id,
];

export const TowerHistorySummaryField = ({ record }) => {
  let sx = {
    fontWeight: "bolder",
  };

  const tower_history = record || useRecordContext();
  if (!types_with_summary.includes(tower_history.type)) {
    return null;
  }

  let summary = tower_history.summary || "";
  summary = summary.toLowerCase();

  switch (summary) {
    case "success":
      sx.color = "green";
      break;
    case "failed":
      sx.color = "red";
      break;
    case "mixed":
      sx.color = "orange";
      break;
    case "empty":
      sx.color = "blue";
      break;

    default:
      break;
  }

  const customClasses = [].filter((v) => v).join(" ");

  return (
    <Link
      to={`/tower-history/${tower_history.id}/show`}
      className="TowerHistorySummaryField"
    >
      <TextField
        record={tower_history}
        source="summary"
        sortable={false}
        sx={sx}
        className={`TowerHistorySummary status-${summary} ${customClasses}`}
      />
    </Link>
  );
};

const statusName = (id, statuses) =>
  (statuses.find((s) => s.id == id) || { name: id }).name;

const formatErrors = (errors) =>
  Object.entries(errors || { "??": "??" })
    .map(([key, value]) => `"${key}" (${value})`)
    .join(", ");

const DetailField = ({ record }) => {
  record = record || useRecordContext();

  let detail = null;
  const error_types_data = get(record, "data.error_types", {}) || {};
  const error_types = Object.keys(error_types_data);
  const unique_error = error_types.length == 1 ? error_types[0] : null;

  if (unique_error) {
    detail = (
      <Box className="ErrorDetail">
        <Box>&apos;{unique_error}&apos;</Box>
        <Box>
          Got events: {get(record, "data.detail.events.first")} to{" "}
          {get(record, "data.detail.events.last")}
        </Box>
        <Box>Expected event: {get(record, "data.next_event.before")}</Box>
      </Box>
    );
  } else {
    let resetType = "Manual";

    switch (record.type) {
      case TowerHistoryType.ErrorEvent.id:
        detail = (
          <Box className="ErrorDetail">
            {get(record, "data.error.description")}
          </Box>
        );
        break;
      case TowerHistoryType.StatusChange.id:
        detail = (
          <Box className="InfoDetail">
            Status changed from &apos;
            {statusName(get(record, "data.old.status_id"), statusChoices)}&apos;
            to &apos;
            {statusName(get(record, "data.new.status_id"), statusChoices)}&apos;
          </Box>
        );
        break;
      case TowerHistoryType.EventsResetAutomaticReset.id:
        resetType = "Automatic"; // eslint-disable-next-line no-fallthrough
      case TowerHistoryType.EventsResetUserReset.id:
        detail = (
          <Box className="WarningDetail">
            {resetType} tower reset using &apos;
            {getFirst(record, ["data.source", "data.new.source"], "???")}&apos;
            because &apos;
            {getFirst(record, ["data.new.reason", "data.reason"], "???")}
            &apos;
          </Box>
        );
        break;
      case TowerHistoryType.EventSync.id:
        switch (getValue(record, "summary", "").toLowerCase()) {
          case "success":
            detail = (
              <Box className="SuccessDetail">
                Synched {get(record, "data.success_count")} events (
                {get(record, "data.detail.success.first")} to{" "}
                {get(record, "data.detail.success.last")}). Next event: &apos;
                {get(record, "data.next_event.after")}&apos;
              </Box>
            );
            break;
          case "mixed":
            detail = (
              <Box className="ErrorDetail">
                Synched {get(record, "data.success_count")} out of{" "}
                {get(record, "data.event_count")} events.
                {get(record, "data.error_count")} events failed because of{" "}
                {formatErrors(get(record, "data.error_types", {}))}. Next event:
                &apos;{get(record, "data.next_event.after")}&apos;
              </Box>
            );
            break;

          default:
            break;
        }
        break;

      default:
        break;
    }
  }

  return <Box className="TowerHistoryDetail">{detail}</Box>;
};

const ReqRefField = () => {
  return (
    <WithRecord
      render={(record) => {
        const req = [
          getValue(record, "data.request.id", "").slice(-4),
          getValue(record, "data.request.packet_key", ""),
        ]
          .filter((v) => String(v).length)
          .join("-");

        if (!req || req == "nown") return null;

        return <Box sx={{ whiteSpace: "nowrap" }}>{req}</Box>;
      }}
    />
  );
};

export const addTimeDiff = (record, index, data) => ({
  ...record,
  _time_diff:
    index > 0
      ? getTimeDiffLabel(
          record.timestamp,
          data[index - 1].timestamp,
          "before",
          "after"
        ) +
        " #" +
        data[index - 1].id
      : "",
});

export const TowerHistoryList = (props) => {
  const title = "Tower History";
  const resource = "tower-history";

  return (
    <MyList
      title={title}
      resource={resource}
      filters={towerHistoryFilters(typeChoices)}
      dataGridProps={{
        rowClick: null,
        omit: ["_time_diff"],
      }}
      sort={{
        field: "id",
        order: "DESC",
      }}
      queryOptions={{
        meta: {
          transformResponse: addTimeDiff,
        },
      }}
      {...props}
    >
      <TextField source="id" />
      <TowerField source="towerId" />
      <TowerTimezonedDateField source="timestamp" showTime />
      <TextField source="_time_diff" />
      <SelectField source="type" choices={typeChoices} />
      <TowerHistorySummaryField source="summary" />
      <DetailField source="detail" sortable={false} />
      <JsonField source="data" sortable={false} collapsed={true} />
      <ReqRefField source="requestId" label="Request id" sortable={false} />
      <MyBooleanField source="isForTest" sortable={false} />
      <UserField source="userId" />
      {Settings.debugging() && (
        <Debug label="Log">
          <LogLink />
        </Debug>
      )}
    </MyList>
  );
};

export const TowerHistoryListView = () => {
  const { towerId } = useParams();

  return <TowerHistoryList towerId={towerId} />;
};

export const TowerHistoryShow = () => {
  return (
    <MyShow>
      <MyTabbedShowLayout>
        <Tab label="Summary" can={true}>
          <RecordSummary>
            <LabeledColumn>
              <TextField source="id" />
              <TowerField source="towerId" />
              <TowerTimezonedDateField source="timestamp" showTime />
              <SelectField source="type" choices={typeChoices} />
              <UserField source="userId" />
            </LabeledColumn>
            <LabeledColumn>
              <MyBooleanField source="isForTest" sortable={false} />
              <TowerHistorySummaryField source="summary" />
              <DetailField source="detail" sortable={false} />
              <ReqRefField source="reqRef" sortable={false} />
              <JsonField source="data" sortable={false} />
              {Settings.debugging() && (
                <Debug label="Log">
                  <LogLink />
                </Debug>
              )}
            </LabeledColumn>
          </RecordSummary>
        </Tab>
      </MyTabbedShowLayout>
    </MyShow>
  );
};
